import { GeoPoint, SlotPlanInfo } from '@@partner:types:app'
import { GQL_ID } from '@@partner:types:graphql'
import { DeliveryZoneFormState } from './types'

import React, { useCallback, useEffect, useState } from 'react'
import { mergeLeft } from 'merge-left-utils'

import { useFetchSingleDeliveryZone } from '@@partner:graphql/query/deliveryZone/singleDeliveryZone.gql'
import { useSaveDeliveryZoneInfo } from '@@partner:graphql/mutation/deliveryZone/saveDeliveryZone.gql'

import {
  Button,
  CheckPicker,
  Col,
  Form,
  Input,
  InputNumber,
  Panel,
  Row,
  SelectPicker
} from 'rsuite'

import FormCheckbox from '@components/common/form/FormCheckbox'
import TimePicker from '@components/common/TimePicker'
import ShopTimezonePicker from '@components/shop/form/ShopTimezonePicker'

import {
  DEFAULT_DELIVERY_ZONE_STATE,
  makeDeliveryZoneFormState,
  mapStateToApoVariable
} from './DeliveryZoneFormState'
import { DeliveryZoneFormValidationModel } from './validation/DeliveryZoneFormValidationModel'

import { useValidatableForm } from '@@partner:helpers/hooks/rsuite'
import { commonFieldChange, getCurrencySign } from '@@partner:helpers/util'
import {
  representTimeWithDateObject
} from '@@partner:helpers/date-utils'
import {
  makeGraphQLErrorNotification,
  notifyError,
  notifyOk
} from '@@partner:helpers/notification'

import {
  DELIVERY_ZONE_STATUS_LABELS,
  EXPRESS_DELIVERY_PROVIDERS,
  SLOT_DELIVERY_PROVIDERS
} from '@@partner:config/deliveryZone'
import DeliveryZoneSlots from './DeliveryZoneSlots'
import { ShopsPicker } from '@components/common/pickers'
import GenerateSlotsModal from './GenerateSlotsModal'
import SortableProviderPicker from './SortableProviderPicker'

type props = {
  zoneId: GQL_ID | null
  newZone: GeoPoint[]
  formId?: string
}

export default function DeliveryZoneForm ({ zoneId, newZone, formId }: props) {
  const { formState, setFormState, setFormRef, setFormErrors } =
    useValidatableForm<DeliveryZoneFormState>(makeDeliveryZoneFormState)
  const [ modalOpen, setModalOpen ] = useState(false)
  const [ generateModalOpen, setGenerateModalOpen ] = useState(false)

  const { data, loading } = useFetchSingleDeliveryZone({
    id: zoneId
  })

  const [ saveZone, { data: updateData, loading: savingDeliveryZone } ] =
    useSaveDeliveryZoneInfo()

  useEffect(() => {
    if (!loading && data?.singleDeliveryZone) {
      setFormState(makeDeliveryZoneFormState(data.singleDeliveryZone))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ data?.singleDeliveryZone ])

  useEffect(() => {
    if (zoneId === null) {
      setFormState(DEFAULT_DELIVERY_ZONE_STATE)
    }
  }, [ setFormState, zoneId ])

  const handleFormChange = useCallback(
    <T extends keyof DeliveryZoneFormState>(
      formData: Record<T, DeliveryZoneFormState[T]>
    ) => {
      setFormState((state) => mergeLeft(state, formData))
    },
    [ setFormState ]
  )

  const handleFormError = useCallback(
    (error: any) => {
      setFormErrors(error)
    },
    [ setFormErrors ]
  )

  const handleFieldChange = commonFieldChange(setFormState)


  const handleSlotChange = useCallback(
    (slotPlans: SlotPlanInfo[]) => {
      setFormState((formState) => ({ ...formState, slotPlans }))
    },
    [ setFormState ]
  )

  useEffect(() => {
    handleFormChange({ zoneGeoDeliveryZone: newZone })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ newZone ])

  const handleFormSubmit = () => {
    ;(isOk = false) => {
      if (isOk) {
        saveZone({
          variables: {
            deliveryZoneInfo: mapStateToApoVariable(formState)
          }
        })
          .then(({ data }) => {
            notifyOk('Зона обновлена')
            setFormState((state) =>
              makeDeliveryZoneFormState(data?.deliveryZoneInfo)
            )
          })
          .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
      }
      else {
        notifyError('Форма заполнена некорректно')
      }
    }
  }

  return (
    <Form
      id={formId}
      model={DeliveryZoneFormValidationModel}
      formValue={formState}
      onChange={handleFormChange}
      onSubmit={(isOk = false) => {
        if (isOk) {
          saveZone({
            variables: {
              deliveryZoneInfo: mapStateToApoVariable(formState)
            }
          })
            .then(({ data }) => {
              notifyOk('Зона обновлена')
              setFormState((state) =>
                makeDeliveryZoneFormState(data?.deliveryZoneInfo)
              )
            })
            .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
        }
        else {
          notifyError('Форма заполнена некорректно')
        }
      }}
      onCheck={handleFormError}
      ref={setFormRef}
      fluid
    >
      <DeliveryZoneSlots 
        slotPlans={formState.slotPlans} 
        onChange={handleSlotChange}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
      { zoneId && (
        <GenerateSlotsModal
          zone_id={zoneId}
          open={generateModalOpen}
          onClose={() => setGenerateModalOpen(false)}
        />
      )}
      <Panel
        shaded
        header={zoneId ? `Зона #${zoneId}` : 'Новая зона'}
        className="my-5"
      >
        <Row>
          <Col md={12} className="my-2">
            <Form.Group>
              <Form.ControlLabel>Название</Form.ControlLabel>
              <Input
                value={formState.name}
                placeholder="Название"
                onChange={handleFieldChange('name')}
                size="sm"
              />
            </Form.Group>
          </Col>
          <Col md={12} className="my-2">
            <ShopsPicker
              value={formState.shop_id ?? null}
              onChange={(value) => handleFieldChange('shop_id')(value ?? undefined)}
              searchable
            />
          </Col>
          <Col md={12} className="my-2">
            <Form.Group>
              <Form.ControlLabel>Статус</Form.ControlLabel>
              <SelectPicker
                data={DELIVERY_ZONE_STATUS_LABELS}
                labelKey="label"
                valueKey="value"
                value={formState.status}
                onChange={handleFieldChange('status')}
                cleanable={false}
                searchable={false}
              />
            </Form.Group>
          </Col>
          <Col md={12} className="my-2">
            <Form.Group>
              <Form.ControlLabel>Часовой пояс</Form.ControlLabel>
              <ShopTimezonePicker
                value={formState.tzOffset || 0}
                onChange={handleFieldChange('tzOffset')}
              />
            </Form.Group>
          </Col>
          <Col md={12} className="my-2">
            <Form.Group>
              <Form.ControlLabel>Приоритет</Form.ControlLabel>
              <Form.Control
                accepter={InputNumber}
                min={0}
                step={1}
                name="customer_order"
                placeholder="Приоритет"
              />
            </Form.Group>
          </Col>
          <Col md={12} className="my-2">
            <Form.Group>
              <Form.ControlLabel>
                Минимальная корзина {getCurrencySign()}
              </Form.ControlLabel>
              <Form.Control
                accepter={InputNumber}
                min={0}
                step={1}
                name="minOrderCost"
                placeholder="Минимальная стоимость заказа"
              />
            </Form.Group>
          </Col>
          <Col md={12} className="my-2">
            <Form.Group>
              <Form.ControlLabel>Обслуживает розницу</Form.ControlLabel>
              <FormCheckbox.Control name="is_serving_retail" />
            </Form.Group>
          </Col>
          <Col md={12} className="my-2">
            <Form.Group>
              <Form.ControlLabel>Обслуживает опт</Form.ControlLabel>
              <FormCheckbox.Control name="is_serving_wholesale" />
            </Form.Group>
          </Col>
        </Row>
      </Panel>
      <Panel shaded className="my-2" header="Доставка в слот">
        <Row>
          <Col xs={24} md={12} className="my-2">
            <Form.Group>
              <Form.ControlLabel>Провайдеры доставки</Form.ControlLabel>
              <SortableProviderPicker
                data={SLOT_DELIVERY_PROVIDERS}
                value={formState.slotDeliveryProviders}
                onChange={handleFieldChange('slotDeliveryProviders')}
              />
            </Form.Group>
          </Col>
          <Col xs={24} md={12} className="my-2">
            <Form.Group>
              <Form.ControlLabel>
               Максимальный вес (кг)
              </Form.ControlLabel>
              <Form.Control
                accepter={InputNumber}
                min={0}
                step={0.1}
                name='zoneSlotMaxWeight'
                placeholder="Максимальный вес"
                disabled={formState.zoneSlotMaxWeightUnlimited}
              />
            </Form.Group>
          </Col>
          <Col xs={24} md={12} className="my-2">
            <Form.Group>
              <Form.ControlLabel>Без ограничения веса</Form.ControlLabel>
              <FormCheckbox.Control name="zoneSlotMaxWeightUnlimited" />
            </Form.Group>
          </Col>
          <Col xs={12} md={12} className="my-2">
            <Form.Group>
              <Form.ControlLabel>Провайдер слотов</Form.ControlLabel>
              <SelectPicker
                data={SLOT_DELIVERY_PROVIDERS}
                value={formState.slotProvider}
                labelKey="label"
                valueKey="value"
                onChange={handleFieldChange('slotProvider')}
                cleanable={false}
                searchable={false}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={12} className="my-2">
            <Form.Group>
              <Form.ControlLabel>Ценовой провайдер</Form.ControlLabel>
              <SelectPicker
                data={SLOT_DELIVERY_PROVIDERS}
                value={formState.slotDeliveryPriceProvider}
                labelKey="label"
                valueKey="value"
                onChange={handleFieldChange('slotDeliveryPriceProvider')}
                cleanable={false}
                searchable={false}
              />
            </Form.Group>
          </Col>
          {
            formState.slotDeliveryPriceProvider !== 'inhouse' && (
              <Col xs={12} md={12} className="my-2">
                <Form.Group>
                  <Form.ControlLabel>Множитель цены </Form.ControlLabel>
                  <Form.Control
                    accepter={InputNumber}
                    min={0}
                    step={0.1}
                    name="slotPriceMultuplier"
                    placeholder="Множитель цены"
                  />
                </Form.Group>
              </Col>
            )
          }
          {
            formState.slotDeliveryPriceProvider !== 'inhouse' && (
              <Col xs={12} md={12} className="my-2">
                <Form.Group>
                  <Form.ControlLabel>Добавочная цена</Form.ControlLabel>
                  <Form.Control
                    accepter={InputNumber}
                    min={0}
                    step={0.1}
                    name="slotPriceAdditional"
                    placeholder="Добавочная цена"
                  />
                </Form.Group>
              </Col>
            )
          }
          <Col xs={24} md={12} className="my-2">
            <Form.Group>
              <Form.ControlLabel>
                {formState.dynamicPrice
                  ? 'Цена по умолчанию'
                  : 'Стоимость доставки'}{' '}
                {getCurrencySign()}
              </Form.ControlLabel>
              <Form.Control
                accepter={InputNumber}
                min={0}
                step={1}
                name="zoneDeliveryCost"
                placeholder="Стоимость доставки"
              />
            </Form.Group>
          </Col>
          <Col xs={24} md={12} className="my-2">
            <Form.Group>
              <Form.ControlLabel>
              Бесплатная доставка от
              </Form.ControlLabel>
              <Form.Control
                accepter={InputNumber}
                min={0}
                step={1}
                name='freeSlotDeliveryCostFrom'
                placeholder="Бесплатная доставка от"
              />
            </Form.Group>
          </Col>
          <Col xs={24} md={12} className="my-2">
            <Form.Group>
              <Form.ControlLabel>
                Стоимость первой доставки {getCurrencySign()}
              </Form.ControlLabel>
              <Form.Control
                accepter={InputNumber}
                min={0}
                step={1}
                name="zoneFirstDeliveryCost"
                placeholder="Стоимость первой доставки"
              />
            </Form.Group>
          </Col>
          <Col xs={24} md={12} className="my-2">
            <Form.Group>
              <Form.ControlLabel>Использовать первую доставку</Form.ControlLabel>
              <FormCheckbox.Control name="useFirstDeliveryPrice" />
            </Form.Group>
          </Col>
          <Button onClick={() => setModalOpen(true)}>
            Изменить расписание слотов
          </Button>
          <Button onClick={() => setGenerateModalOpen(true)}>
            Пересоздать слоты
          </Button>
        </Row>
      </Panel>
      <Panel shaded className="my-5">
        <Row>
          <Col xs={24} md={12} className="my-2">
            <Form.Group>
              <Form.ControlLabel>Экспресс-доставка включена</Form.ControlLabel>
              <FormCheckbox.Control name="zoneHasExpressDeliveryEnabled" />
            </Form.Group>
          </Col>
          <Col xs={24} md={12} className="my-2">
            <Form.Group>
              <Form.ControlLabel>
              Максимальный вес (кг)
              </Form.ControlLabel>
              <Form.Control
                accepter={InputNumber}
                min={0}
                step={0.1}
                name='zoneExpressMaxWeight'
                placeholder="Максимальный вес"
                disabled={formState.zoneExpressMaxWeightUnlimited}
              />
            </Form.Group>
          </Col>
          <Col xs={24} md={12} className="my-2">
            <Form.Group>
              <Form.ControlLabel>Без ограничения веса</Form.ControlLabel>
              <FormCheckbox.Control name="zoneExpressMaxWeightUnlimited" />
            </Form.Group>
          </Col>
          {formState.zoneHasExpressDeliveryEnabled && (
            <Row>
              <Col xs={24} md={12} className="my-2">
                <Form.Group>
                  <Form.ControlLabel>Провайдеры доставки</Form.ControlLabel>
                  <SortableProviderPicker
                    data={EXPRESS_DELIVERY_PROVIDERS}
                    value={formState.expressDeliveryProviders}
                    onChange={handleFieldChange('expressDeliveryProviders')}
                  />
                </Form.Group>
              </Col>

              <Col xs={12} md={12} className="my-2">
                <Form.Group>
                  <Form.ControlLabel>Ценовой провайдер</Form.ControlLabel>
                  <SelectPicker
                    data={EXPRESS_DELIVERY_PROVIDERS}
                    value={formState.expressDeliveryPriceProvider}
                    labelKey="label"
                    valueKey="value"
                    onChange={handleFieldChange('expressDeliveryPriceProvider')}
                    cleanable={false}
                    searchable={false}
                  />
                </Form.Group>
              </Col>
              {
                formState.expressDeliveryPriceProvider !== 'inhouseExpress' && (
                  <Col xs={12} md={12} className="my-2">
                    <Form.Group>
                      <Form.ControlLabel>Множитель цены </Form.ControlLabel>
                      <Form.Control
                        accepter={InputNumber}
                        min={0}
                        step={0.1}
                        name="expressPriceMultuplier"
                        placeholder="Множитель цены"
                      />
                    </Form.Group>
                  </Col>
                )
              }
              {
                formState.expressDeliveryPriceProvider !== 'inhouseExpress' && (
                  <Col xs={12} md={12} className="my-2">
                    <Form.Group>
                      <Form.ControlLabel>Добавочная цена</Form.ControlLabel>
                      <Form.Control
                        accepter={InputNumber}
                        min={0}
                        step={0.1}
                        name="expressPriceAdditional"
                        placeholder="Добавочная цена"
                      />
                    </Form.Group>
                  </Col>
                )
              }
              <Col xs={24} md={12} className="my-2">
                <Form.Group>
                  <Form.ControlLabel>
                    Стоимость экспресс-доставки {getCurrencySign()}
                  </Form.ControlLabel>
                  <Form.Control
                    accepter={InputNumber}
                    min={0}
                    step={1}
                    name="zoneExpressDeliveryCost"
                    placeholder="Стоимость экспресс-доставки"
                  />
                </Form.Group>
              </Col>
              <Col xs={24} md={12} className="my-2">
                <Form.Group>
                  <Form.ControlLabel>
                    Время экспресс-доставки (в минутах)
                  </Form.ControlLabel>
                  <Form.Control
                    accepter={InputNumber}
                    min={0}
                    step={1}
                    name="expressDeliveryMaxShippingTime"
                    placeholder="Время экспресс-доставки"
                  />
                </Form.Group>
              </Col>
              <Col xs={24} md={12} className="my-2">
                <Form.Group>
                  <Form.ControlLabel>
                    Время начала экспресс доставки{' '}
                  </Form.ControlLabel>
                  <div className="w-1/2">
                    <TimePicker
                      value={representTimeWithDateObject(
                        formState.zoneTsExpressDeliveryStart,
                        true
                      )}
                      onSelect={(date) =>
                        handleFieldChange('zoneTsExpressDeliveryStart')(
                          date
                        )
                      }
                    />
                  </div>
                </Form.Group>
              </Col>
              <Col xs={24} md={12} className="my-2">
                <Form.Group>
                  <Form.ControlLabel>
                    Время окончания экспресс доставки{' '}
                  </Form.ControlLabel>
                  <div className="w-1/2">
                    <TimePicker
                      value={representTimeWithDateObject(
                        formState.zoneTsExpressDeliveryFinish,
                        true
                      )}
                      onSelect={(date) =>
                        handleFieldChange('zoneTsExpressDeliveryFinish')(
                          date
                        )
                      }
                    />
                  </div>
                </Form.Group>
              </Col>
              <Col xs={24} md={12} className="my-2">
                <Form.Group>
                  <Form.ControlLabel>
                  Бесплатная доставка от
                  </Form.ControlLabel>
                  <Form.Control
                    accepter={InputNumber}
                    min={0}
                    step={1}
                    name='freeExpressDeliveryCostFrom'
                    placeholder="Бесплатная доставка от"
                  />
                </Form.Group>
              </Col>
            </Row>
          )}
        </Row>
      </Panel>
      <Button
        className="u-btn-submit"
        appearance="primary"
        color="violet"
        type="submit"
        size="lg"
      >
        Cохранить изменения
      </Button>
    </Form>
  )
}
